import dayjs from "dayjs";

export const formatDate = (isoDate: string) => {
  const [year, month, day] = isoDate?.split("T")[0]?.split("-")?.map(Number);

  const date = new Date(year, month - 1, day); // Local date without time shift

  const options: Intl.DateTimeFormatOptions = {
    month: "short",
    day: "numeric",
    year: "numeric",
  };

  return date.toLocaleDateString("en-US", options);
};

export function getErrorMessage(
  error: unknown,
  fallback = "Something went wrong!",
): string {
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string"
  ) {
    return (error as { message: string }).message;
  }
  return fallback;
}

export type SortOrder = "asc" | "desc";

export const sortData = (
  data: any[],
  orderBy: string,
  order: SortOrder
, isItinTable = false

): any[] => {
  if (!data || !orderBy) return data || [];
// console.log(data,"datat")
  // ✅ Map table headings to actual data keys
  const keyMap: Record<string, string> = {
    "ORDER ID": "order_id",
   COMPANY: "sku",
    "ORDER CREATED": "created_at",
    "LLC STATUS": "llc_status",
    "EIN STATUS": "ein_status",
   "ITIN Applicant":"sku"

  };

  const key = keyMap[orderBy];
  if (!key) return data; // if column not sortable (like ACTION)

  return [...data].sort((a, b) => {
    let valueA = a[key];
    let valueB = b[key];

    // ✅ Handle date sorting
    if (key === "created_at") {
      const dateA = dayjs(valueA);
      const dateB = dayjs(valueB);
      return order === "asc" ? dateA.diff(dateB) : dateB.diff(dateA);
    }

    // ✅ Handle string comparison (case-insensitive)
    if (typeof valueA === "string" && typeof valueB === "string") {
      valueA = valueA.toLowerCase();
      valueB = valueB.toLowerCase();
      if (valueA < valueB) return order === "asc" ? -1 : 1;
      if (valueA > valueB) return order === "asc" ? 1 : -1;
      return 0;
    }

    // ✅ Handle number comparison
    if (typeof valueA === "number" && typeof valueB === "number") {
      return order === "asc" ? valueA - valueB : valueB - valueA;
    }

    // ✅ Default fallback
    return 0;
  });
};

export const formatToTimeZone = (
  dateInput: string | Date,
  timeZone: string = Intl.DateTimeFormat().resolvedOptions().timeZone,
  locale: string = "en-US",
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "short",
    day: "2-digit",
  }
): string => {
  if (!dateInput) return "Invalid Date";

  const date = new Date(
    typeof dateInput === "string" && !dateInput.endsWith("Z")
      ? dateInput + "Z"
      : dateInput
  );

  if (isNaN(date.getTime())) {
    return "Invalid Date";
  }

  return new Intl.DateTimeFormat(locale, {
    timeZone,
    ...options,
  }).format(date);
};

export async function decryptKeys(
  encryptedSecretKey: string,
  encryptedPublishableKey: string,
  hexSecretKey: string
): Promise<{ secretKey: string; publishableKey: string }> {
  const secretKeyBytes = hexStringToUint8Array(hexSecretKey);

  const secretKey = await decryptAESGCM(encryptedSecretKey, secretKeyBytes);
  const publishableKey = await decryptAESGCM(
    encryptedPublishableKey,
    secretKeyBytes
  );

  return { secretKey, publishableKey };
}

async function decryptAESGCM(
  base64UrlData: string,
  keyBytes: Uint8Array
): Promise<string> {
  const encryptedBytes = base64UrlToUint8Array(base64UrlData);
  const nonce = encryptedBytes.slice(0, 12); // AES-GCM nonce is 12 bytes
  const ciphertext = encryptedBytes.slice(12);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["decrypt"]
  );

  const decryptedBuffer = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: nonce },
    cryptoKey,
    ciphertext
  );

  return new TextDecoder().decode(decryptedBuffer);
}

// Helper: Convert base64url to Uint8Array
function base64UrlToUint8Array(base64Url: string): Uint8Array {
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const padded = base64.padEnd(
    base64.length + ((4 - (base64.length % 4)) % 4),
    "="
  );
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

// Helper: Convert hex string to Uint8Array
function hexStringToUint8Array(hex: string): Uint8Array {
  if (![32, 64].includes(hex.length)) {
    throw new Error(
      "AES key must be 128-bit (32 hex chars) or 256-bit (64 hex chars)"
    );
  }

  const result = new Uint8Array(hex.length / 2);
  for (let i = 0; i < hex.length; i += 2) {
    result[i / 2] = parseInt(hex.substr(i, 2), 16);
  }
  return result;
}

export async function encryptKeys(
  secretKey: string,
  hexSecretKey: string
): Promise<{ secretKey: string }> {
  const secretKeyBytes = hexStringToUint8Array(hexSecretKey);

  const encryptedSecretKey = await encryptAESGCM(secretKey, secretKeyBytes);

  return {
    secretKey: encryptedSecretKey,
  };
}

async function encryptAESGCM(
  plaintext: string,
  keyBytes: Uint8Array
): Promise<string> {
  const nonce = crypto.getRandomValues(new Uint8Array(12)); // 12-byte nonce
  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    keyBytes,
    { name: "AES-GCM" },
    false,
    ["encrypt"]
  );
  const encoded = new TextEncoder().encode(plaintext);
  const ciphertext = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv: nonce },
    cryptoKey,
    encoded
  );
  // Concatenate nonce + ciphertext
  const combined = new Uint8Array(nonce.length + ciphertext.byteLength);
  combined.set(nonce, 0);
  combined.set(new Uint8Array(ciphertext), nonce.length);
  // Convert to base64url
  return uint8ArrayToBase64Url(combined);
}

function uint8ArrayToBase64Url(bytes: Uint8Array): string {
  let binary = "";
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  let base64 = btoa(binary);
  // Convert to base64url
  return base64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
